import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { appointmentsApi } from "@/api/appointments";
import type {
  AppointmentAvailabilityRequest,
  AppointmentConflictCheckRequest,
  AppointmentListQuery,
  CreateAppointmentDto,
  RecurrenceConflictRequest,
  RecurrenceEditScope,
  UpdateAppointmentDto,
} from "@/api/appointments";
import type { WorkTaskRelatedEntityType } from "@/api/workTasks";
import { appointmentKeys } from "./appointmentKeys";

/**
 * Calendar Appointments v1.0 — React Query hooks. All mutations invalidate
 * the "appointments" query-key family so the calendar grid, day drawer,
 * edit modal, and the dashboard "Upcoming" widget stay in sync after any
 * change. Mirrors the Tasks module's <c>useWorkTasks</c>.
 */

export function useAppointments(range: AppointmentListQuery) {
  return useQuery({
    queryKey: appointmentKeys.list(range),
    queryFn: () => appointmentsApi.list(range),
  });
}

export function useAppointment(id: string | undefined) {
  return useQuery({
    queryKey: id
      ? appointmentKeys.detail(id)
      : appointmentKeys.detail("missing"),
    queryFn: () => appointmentsApi.get(id!),
    enabled: Boolean(id),
  });
}

/**
 * Every non-cancelled appointment linked to one entity — the Calendar tab on
 * entity detail pages. Mirrors <c>useTasksByRelated</c>: disabled until an id
 * is present and never fires for the <c>None</c> sentinel.
 *
 * The response is NOT own-scope — see <c>appointmentsApi.listByRelated</c>.
 * Rows the viewer neither owns nor attends carry <c>isRedacted</c> with their
 * content blanked, so consumers must branch on that flag rather than assuming
 * a title is present.
 */
export function useAppointmentsByRelated(
  type: WorkTaskRelatedEntityType,
  entityId: string | undefined,
) {
  return useQuery({
    queryKey: appointmentKeys.byRelated(type, entityId ?? "missing"),
    queryFn: () => appointmentsApi.listByRelated(type, entityId!),
    enabled: Boolean(entityId) && type !== "None",
  });
}

export function useUpcomingAppointments(limit = 5) {
  return useQuery({
    queryKey: appointmentKeys.upcoming(limit),
    queryFn: () => appointmentsApi.upcoming(limit),
  });
}

/**
 * Informational, non-blocking attendee conflict check. Read-only (no cache
 * mutation) — mirrors {@link useAiParseAppointment}. Pass the (debounced)
 * request payload, or `null` to disable (e.g. no Team Members selected or the
 * times are invalid). Callers must never gate Create/Save on the result.
 */
export function useConflictCheck(input: AppointmentConflictCheckRequest | null) {
  return useQuery({
    queryKey: appointmentKeys.conflictCheck(input),
    queryFn: () => appointmentsApi.conflictCheck(input!),
    enabled: Boolean(input),
    // A conflict set is only meaningful for the current draft window; keep it
    // briefly fresh so identical rapid edits don't re-hit the API.
    staleTime: 30_000,
  });
}

/**
 * Per-day attendee availability for the modal's busy/free strip. Read-only,
 * same shape as {@link useConflictCheck} — pass the (debounced) request payload
 * or `null` to disable. Runs alongside the conflict check off the same debounce
 * tick, so the strip adds no latency on top of the existing badge. Advisory
 * only: callers must never gate Create/Save on the result.
 */
export function useAvailability(input: AppointmentAvailabilityRequest | null) {
  return useQuery({
    queryKey: appointmentKeys.availability(input),
    queryFn: () => appointmentsApi.availability(input!),
    enabled: Boolean(input),
    staleTime: 30_000,
  });
}

/**
 * Bounded busy check across a proposed repeat rule's next few occurrences.
 *
 * Its own endpoint, not a widened {@link useConflictCheck}: that one treats
 * presence-in-the-response as the conflict signal, so stretching its window
 * across a whole series would flag every appointment inside it.
 *
 * Fires ONLY when a repeat rule is set, so a one-off appointment issues exactly
 * the same two requests per Start change it always has.
 */
export function useRecurrenceConflicts(input: RecurrenceConflictRequest | null) {
  return useQuery({
    queryKey: appointmentKeys.recurrenceConflicts(input),
    queryFn: () => appointmentsApi.recurrenceConflicts(input!),
    enabled: Boolean(input),
    staleTime: 30_000,
  });
}

export function useCreateAppointment() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: CreateAppointmentDto) =>
      appointmentsApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

/**
 * Calendar AI Create+ — parse pasted text into a structured appointment draft.
 * Read-only (no cache mutation); the panel feeds the result into the form.
 */
export function useAiParseAppointment() {
  return useMutation({
    // no-invalidation: read-only AI parse — returns a draft, writes no cache.
    mutationFn: ({ rawText, timezone }: { rawText: string; timezone?: string }) =>
      appointmentsApi.aiParse(rawText, timezone),
  });
}

export function useUpdateAppointment(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: UpdateAppointmentDto) =>
      appointmentsApi.update(id, payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useCancelAppointment() {
  const qc = useQueryClient();
  return useMutation({
    // The scope is optional and only meaningful on an occurrence of a series;
    // a caller that omits it cancels one row, exactly as before.
    mutationFn: ({ id, scope }: { id: string; scope?: RecurrenceEditScope }) =>
      appointmentsApi.cancel(id, scope),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

/**
 * Re-send the current invitation to external attendees. Sends nothing new about
 * the meeting, so nothing cached about it changes — but the appointment list is
 * still invalidated so any surface showing send state stays honest.
 */
export function useResendInvitations() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      emails,
      includeInternalAttendees,
    }: {
      id: string;
      emails?: string[];
      includeInternalAttendees?: boolean;
    }) =>
      appointmentsApi.resendInvitations(id, emails, includeInternalAttendees ?? false),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useAddAttendees(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userIds: string[]) => appointmentsApi.addAttendees(id, userIds),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useRemoveAttendee(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => appointmentsApi.removeAttendee(id, userId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}

export function useRespond(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (response: "Accepted" | "Declined") =>
      appointmentsApi.respond(id, response),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: appointmentKeys.all });
    },
  });
}
